import React, { useState } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { sendVerificationEmail, getEmailVerificationRedirectUrl } from '../../base';

const EmailVerificationPendingModal = ({ show, onClose, userEmail, userName }) => {
  const { t } = useTranslation();
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [resendCount, setResendCount] = useState(0);

  const handleResendVerification = async () => {
    if (resendCount >= 3) {
      setResendError(t('emailVerificationResendLimit'));
      return;
    }

    setIsResending(true);
    setResendError(null);
    
    try {
      const actionCodeSettings = {
        url: getEmailVerificationRedirectUrl(),
        handleCodeInApp: true,
      };
      
      await sendVerificationEmail(actionCodeSettings);
      
      setResendSuccess(true);
      setResendCount(prev => prev + 1);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setResendSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error resending verification email:', error);
      
      if (error.code === 'auth/too-many-requests') {
        setResendError(t('emailVerificationTooManyRequests'));
      } else if (error?.message === 'No user is signed in.') {
        setResendError(t('emailVerificationNoActiveSession', {
          defaultValue: 'Your session has expired. Please sign in and try again.'
        }));
      } else {
        setResendError(t('emailVerificationResendError'));
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      backdrop={true}
      keyboard={true}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title className="w-100 text-center">
          <i className="fas fa-envelope-open-text mb-2" style={{ fontSize: '2rem', color: '#007bff' }}></i>
          <div>{t('emailVerificationPendingTitle')}</div>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="text-center d-flex flex-column">
        <div className="mb-3 w-100">
          <p className="mb-2">
            {t('emailVerificationPendingMessage', { 
              email: userEmail,
              name: userName 
            })}
          </p>
          <div
            className="border rounded p-3 mt-3 w-100"
            style={{ backgroundColor: '#eef6ff', borderColor: '#b9d8ff' }}
          >
            <div className="fw-semibold mb-2">
              {t('emailVerificationNextStepTitle', 'What to do now')}
            </div>
            <div className="text-start" style={{ maxWidth: '520px', margin: '0 auto' }}>
              <div>{t('emailVerificationNextStep1', '1. Open your inbox')}</div>
              <div>{t('emailVerificationNextStep2', '2. Click the verification link in the email')}</div>
              <div>{t('emailVerificationNextStep3', '3. Come back here. We will continue automatically once the email is verified')}</div>
            </div>
          </div>
        </div>

        <div
          className="border rounded p-3 mb-3 w-100 text-start"
          style={{
            backgroundColor: '#fff4db',
            borderColor: '#f0c36d',
            boxShadow: '0 6px 18px rgba(240, 195, 109, 0.18)'
          }}
        >
          <div className="d-flex align-items-start">
            <div
              className="me-3 d-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: '36px',
                height: '36px',
                minWidth: '36px',
                backgroundColor: '#f7b84b',
                color: '#5c3b00',
                fontSize: '1rem',
                fontWeight: 700
              }}
            >
              !
            </div>
            <div>
              <div className="fw-semibold mb-1">
                {t('emailVerificationTips', 'Helpful tips')}
              </div>
              <div style={{ color: '#6b4a09', lineHeight: 1.45 }}>
                {t('emailVerificationTipSpam')}
              </div>
            </div>
          </div>
        </div>

        <div className="border rounded p-3 mb-3 w-100" style={{ backgroundColor: '#f8f9fa' }}>
          <small className="text-muted">
            <strong>{t('emailVerificationTips')}:</strong>
            <ul className="mt-2 mb-0 text-start">
              <li>{t('emailVerificationTipTime')}</li>
              <li>{t('emailVerificationTipRefresh')}</li>
            </ul>
          </small>
        </div>

        {resendSuccess && (
          <Alert variant="success" className="mb-3">
            <i className="fas fa-check-circle me-2"></i>
            {t('emailVerificationResendSuccess')}
          </Alert>
        )}

        {resendError && (
          <Alert variant="danger" className="mb-3">
            <i className="fas fa-exclamation-circle me-2"></i>
            {resendError}
          </Alert>
        )}
      </Modal.Body>
      
      <Modal.Footer className="d-flex flex-column align-items-center border-0">
        <Button 
          variant="outline-primary" 
          onClick={handleResendVerification}
          disabled={isResending || resendCount >= 3}
          className="mb-3"
        >
          {isResending ? (
            <>
              <Spinner size="sm" className="me-2" />
              {t('emailVerificationResending')}
            </>
          ) : (
            <>
              <i className="fas fa-redo-alt me-2"></i>
              {t('emailVerificationResendButton')}
              {resendCount > 0 && ` (${resendCount}/3)`}
            </>
          )}
        </Button>

        <Button
          variant="link"
          onClick={handleClose}
          className="mb-2 p-0"
        >
          {t('emailVerificationBackToLoginExplicit', 'I already verified or want to go back to sign in')}
        </Button>
        
        <div className="text-center">
          <small className="text-muted">
            {t('emailVerificationAutoClose', 'This window will close automatically once you verify your email')}
          </small>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EmailVerificationPendingModal;
